import {
  analyticsCaptureRequestSchema,
  type AnalyticsCaptureRequest,
  type AnalyticsEventName,
  type ParsedAnalyticsCaptureRequest,
} from "@meta-video/contracts";

export interface AnalyticsClient {
  capture(request: AnalyticsCaptureRequest): void;
}

type EventRequest<Name extends AnalyticsEventName> = Extract<
  AnalyticsCaptureRequest,
  { readonly event: Name }
>;
type EventProperties<Name extends AnalyticsEventName> = EventRequest<Name>["properties"];

function event<Name extends AnalyticsEventName>(
  name: Name,
  sessionId: string,
  properties: EventProperties<Name>,
): EventRequest<Name> {
  return {
    event: name,
    properties,
    sessionId,
  } as EventRequest<Name>;
}

export const analyticsEvents = {
  adminAction(
    sessionId: string,
    properties: EventProperties<"admin action completed">,
  ) {
    return event("admin action completed", sessionId, properties);
  },
  comparisonSelectionChanged(
    sessionId: string,
    properties: EventProperties<"comparison selection changed">,
  ) {
    return event("comparison selection changed", sessionId, properties);
  },
  comparisonViewed(
    sessionId: string,
    properties: EventProperties<"comparison viewed">,
  ) {
    return event("comparison viewed", sessionId, properties);
  },
  filtersApplied(
    sessionId: string,
    properties: EventProperties<"filters applied">,
  ) {
    return event("filters applied", sessionId, properties);
  },
  filtersReset(
    sessionId: string,
    properties: EventProperties<"filters reset">,
  ) {
    return event("filters reset", sessionId, properties);
  },
  pageview(sessionId: string, properties: EventProperties<"$pageview">) {
    return event("$pageview", sessionId, properties);
  },
  playbackStarted(
    sessionId: string,
    properties: EventProperties<"video playback started">,
  ) {
    return event("video playback started", sessionId, properties);
  },
  reportInteraction(
    sessionId: string,
    properties: EventProperties<"report interaction completed">,
  ) {
    return event("report interaction completed", sessionId, properties);
  },
  resultsPageChanged(
    sessionId: string,
    properties: EventProperties<"results page changed">,
  ) {
    return event("results page changed", sessionId, properties);
  },
  savedViewAction(
    sessionId: string,
    properties: EventProperties<"saved view action completed">,
  ) {
    return event("saved view action completed", sessionId, properties);
  },
  searchSubmitted(
    sessionId: string,
    properties: EventProperties<"search submitted">,
  ) {
    return event("search submitted", sessionId, properties);
  },
} as const;

export function createAnalyticsClient(
  send: (request: ParsedAnalyticsCaptureRequest) => void | Promise<void>,
): AnalyticsClient {
  return {
    capture(request) {
      const parsed = analyticsCaptureRequestSchema.safeParse(request);
      if (!parsed.success) return;
      try {
        void Promise.resolve(send(parsed.data)).catch(() => undefined);
      } catch {
        // Analytics delivery is deliberately best-effort and must never affect
        // the product action that produced the event.
      }
    },
  };
}

export const disabledAnalyticsClient: AnalyticsClient = Object.freeze({
  capture() {},
});
